/**
 * L2 Adaptive Routing — pure module that maps a member's self-reported
 * activation (0–10, where 0 = calm and 10 = maxed out) to a recommended
 * sequence of modalities from the Nervous System Reset toolkit.
 *
 * Rules were chosen so that:
 *   • High activation (8–10) leads with *downshift* practices (grounding,
 *     long-exhale breathing) BEFORE any bilateral rhythm work — bilateral
 *     stimulation is more effective once the sympathetic surge is off.
 *   • Moderate activation (4–7) leads with resonance breathing (5-5) to
 *     bring HRV into coherence, then bilateral rhythm to consolidate,
 *     then a grounding close.
 *   • Low activation (0–3) treats the session as maintenance / practice:
 *     bilateral rhythm for focus, coherence breathing to steady, journal
 *     reflection to consolidate any noticing.
 *
 * The `chain` order matters — it is the recommended sequence, not just
 * "which modalities apply". Modality codes match the TOOLKIT entries in
 * Welcome.jsx so the UI can highlight them in place.
 */

const BAND_HIGH = {
  key: "high",
  label: "High activation",
  detail: "Let's start by settling your body before anything else.",
  tone: "red",
  chain: [
    {
      code: "TK.02",
      title: "Grounding",
      why: "5-4-3-2-1 sensory anchor — brings you out of the surge.",
    },
    {
      code: "TK.01",
      title: "Cyclic Sighing (4-7-8)",
      why: "Long-exhale breathing engages the vagal brake.",
      pattern: "4-7-8",
    },
    {
      code: "TK.03",
      title: "Bilateral Rhythm",
      why: "Once the surge softens, bilateral rhythm helps consolidate.",
    },
  ],
  ctaLabel: "Start with Grounding",
};

const BAND_MODERATE = {
  key: "moderate",
  label: "Moderate activation",
  detail: "A balanced reset — breath in, rhythm through, ground out.",
  tone: "amber",
  chain: [
    {
      code: "TK.01",
      title: "Coherence Breathwork (5-5)",
      why: "Resonance breathing brings HRV into coherence.",
      pattern: "5-5",
    },
    {
      code: "TK.03",
      title: "Bilateral Rhythm",
      why: "Consolidate the shift with alternating rhythm.",
    },
    {
      code: "TK.02",
      title: "Grounding",
      why: "5-4-3-2-1 exit — closes the session safely.",
    },
  ],
  ctaLabel: "Start with Coherence Breathwork",
};

const BAND_LOW = {
  key: "low",
  label: "Calm / low activation",
  detail: "Treat this as practice — build the skill while you're already steady.",
  tone: "blue",
  chain: [
    {
      code: "TK.03",
      title: "Bilateral Rhythm",
      why: "Reinforces focus and cross-hemispheric flow.",
    },
    {
      code: "TK.01",
      title: "Coherence Breathwork (5-5)",
      why: "Practice resonance breathing while it's easy.",
      pattern: "5-5",
    },
    {
      code: "TK.06",
      title: "Journal Reflection",
      why: "Log a small noticing to reinforce the habit.",
    },
  ],
  ctaLabel: "Start with Bilateral Rhythm",
};

/**
 * getAdaptivePlan(activation) — returns the recommended plan for an
 * activation reading 0–10. Returns null when activation is null/undefined
 * so callers can render a neutral default state.
 */
export function getAdaptivePlan(activation) {
  if (activation === null || activation === undefined) return null;
  const a = Number(activation);
  if (Number.isNaN(a)) return null;
  if (a >= 8) return BAND_HIGH;
  if (a >= 4) return BAND_MODERATE;
  return BAND_LOW;
}

/**
 * getAdaptiveCtaLabel(activation, fallback) — small helper for the hero
 * CTA button so callers don't have to null-check the plan themselves.
 */
export function getAdaptiveCtaLabel(activation, fallback) {
  const plan = getAdaptivePlan(activation);
  return plan ? plan.ctaLabel : fallback;
}

/**
 * getHighlightedCodes(activation) — returns a Set of TOOLKIT codes that
 * the current plan recommends, so the Toolkit grid can highlight them.
 */
export function getHighlightedCodes(activation) {
  const plan = getAdaptivePlan(activation);
  if (!plan) return new Set();
  return new Set(plan.chain.map((step) => step.code));
}

/**
 * getBandTint(tone) — CSS color tokens for each activation band. Kept
 * here so the routing rules and their visual language stay co-located.
 */
export function getBandTint(tone) {
  switch (tone) {
    case "red":
      return {
        border: "var(--iw-accent-red, #d90429)",
        surface: "linear-gradient(180deg, #FBF3F3 0%, #FFF 70%)",
        label: "var(--iw-accent-red, #d90429)",
      };
    case "amber":
      return {
        border: "#B8860B",
        surface: "linear-gradient(180deg, #FBF7EC 0%, #FFF 70%)",
        label: "#8B6508",
      };
    case "blue":
    default:
      return {
        border: "var(--iw-blue, #1e3a8a)",
        surface: "linear-gradient(180deg, #EEF3FB 0%, #FFF 70%)",
        label: "var(--iw-blue, #1e3a8a)",
      };
  }
}
